/** 文本元素展示层（编辑由自研 TextEditOverlay 接管）。 */
import '@/components/canvas/shared/styles/page-text-static.css';

import { useEffect, useRef } from 'react';
import type { TextElementComponentProps } from '@/components/canvas/elements/types';
import { appendBlurFilter, composeContainerTransform } from '../group/containerTransform';
import { detectBlockKind } from '@/components/canvas/elements/text/utils/textContent';
import { cornerRadiiToCssBorderRadius, resolveCornerRadiiPx } from '@/components/canvas/shared/utils/geometry/cornerRadius';
import { elementFillCss, firstShadow, firstStroke, blurRadius, shadowToCss } from '@/components/canvas/elements/model';
import { TextDocumentRenderer } from './renderer/TextDocumentRenderer';
import { getTextLayoutFromElement, isVerticalTextWritingMode } from './model/textStyles';

// ─── component ────────────────────────────────────────────────────────────────

export function TextElement({
  element,
  selected,
  dragOffset = { dx: 0, dy: 0 },
  onMouseDown,
  onStartEdit,
}: TextElementComponentProps) {
  const { dx, dy } = dragOffset;
  /**
   * 用于检测"点击（无拖拽）+ 已选中"→ 进入编辑。
   * mousedown.preventDefault() 会阻止原生 click 事件，但不影响 pointer 事件链，
   * 因此用 pointerdown/pointerup 捕获这一判断。
   */
  const abortRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => { abortRef.current?.abort(); };
  }, []);

  const { paragraph, writingMode } = getTextLayoutFromElement(element);
  const isVertical = isVerticalTextWritingMode(writingMode);
  const padding = element.size.padding;
  const stroke = firstStroke(element.appearance);
  const shadow = firstShadow(element.appearance);
  const textAlign = paragraph.textAlign ?? 'left';
  const blockKind = detectBlockKind(element.data.document);
  const isListBlock = blockKind === 'unordered-list-item' || blockKind === 'ordered-list-item';
  const verticalAlign = element.paragraph.verticalAlign ?? 'middle';
  const alignItems =
    verticalAlign === 'top'
      ? 'flex-start'
      : verticalAlign === 'bottom'
        ? 'flex-end'
        : 'center';

  const style: React.CSSProperties = {
    position: 'absolute',
    left: element.layout.x + dx,
    top: element.layout.y + dy,
    width: element.size.width,
    height: element.size.height,
    boxSizing: 'border-box',
    opacity: element.appearance.opacity,
    transform: composeContainerTransform(element),
    boxShadow: shadowToCss(shadow),
    filter: appendBlurFilter(undefined, blurRadius(element)),
    writingMode,
    display: 'flex',
    alignItems,
    justifyContent: isVertical ? 'flex-start' : undefined,
    overflow: isListBlock ? 'visible' : 'hidden',
    paddingTop: padding?.top ?? 8,
    paddingRight: padding?.right ?? 8,
    paddingBottom: padding?.bottom ?? 8,
    paddingLeft: padding?.left ?? 8,
    backgroundColor: elementFillCss(element),
    borderWidth: stroke?.width,
    borderColor: stroke?.color,
    borderStyle: stroke?.style,
    borderRadius: cornerRadiiToCssBorderRadius(resolveCornerRadiiPx(element)),
    cursor: 'move',
  };

  return (
    <div
      style={style}
      className="pointer-events-auto"
      data-element-hit
      data-element-id={element.id}
      onPointerDown={(e) => {
        // 右键等非主键不参与「点击进编辑」，避免 pointerup 把多选收成单选
        if (e.button !== 0) return;
        // 记录按下时的状态，用于后续判断是否应进入编辑
        const wasSelected = selected;
        const startX = e.clientX;
        const startY = e.clientY;
        let hasMoved = false;

        // 取消上一次未完成的追踪（极速连点保护）
        abortRef.current?.abort();
        const controller = new AbortController();
        abortRef.current = controller;

        const onMove = (me: PointerEvent) => {
          if (Math.abs(me.clientX - startX) > 4 || Math.abs(me.clientY - startY) > 4) {
            hasMoved = true;
          }
        };

        const onUp = (ue: PointerEvent) => {
          controller.abort(); // 移除 pointermove 监听
          if (!hasMoved && wasSelected && !element.interaction.locked) {
            // 已选中 + 未拖拽 = 点击进入编辑
            const isList = blockKind === 'unordered-list-item' || blockKind === 'ordered-list-item';
            onStartEdit?.(element.id, isList ? undefined : { clickX: ue.clientX, clickY: ue.clientY });
          }
        };

        // 使用捕获阶段确保能在画布拖拽系统之前感知到 pointer 事件
        window.addEventListener('pointermove', onMove, { signal: controller.signal, capture: true });
        window.addEventListener('pointerup', onUp, { signal: controller.signal, once: true, capture: true });
      }}
      onMouseDown={(e) => {
        e.stopPropagation();
        if (element.interaction.locked) return;
        // 统一委托给画布拖拽系统，与其他元素行为一致：
        // 未选中时 → 选中 + 开始拖拽；已选中时 → 直接开始拖拽
        onMouseDown?.(e, element);
      }}
      onClick={(e) => e.stopPropagation()}
      // 双击：进入编辑并全选文本（第二次单击进入编辑时会再次调用 onStartEdit，
      // dblclick 携带 selectAll:true 覆盖，最终效果是全选）
      onDoubleClick={(e) => {
        e.stopPropagation();
        if (element.interaction.locked) return;
        onStartEdit?.(element.id, { selectAll: true });
      }}
    >
      <TextDocumentRenderer element={element} />
    </div>
  );
}
